import React from "react";
import styles from "./ClientsPage.module.css";
import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import * as Icon from "react-bootstrap-icons";
import { ThreeCircles } from "react-loader-spinner";
import ClientDocumentsPage from "../ClientDocuments/ClientDocumentsPage";
import { getClients, searchClient } from "./ClientsPage.service";
import { ClientDataItem, ClientsPageProps } from "./ClientsPage.types";
import type { ClientScope } from "./ClientsPage.types";
import { CygnusGrid } from "@cygnusaiinternal/cygnus-common";
import { GridEntityNameCell } from "reusable-components/GridEntityNameCell/GridEntityNameCell";
import { GridSearchInput } from "reusable-components/GridSearchInput/GridSearchInput";
import { ViewK1ActionCell } from "./components/ViewK1ActionCell/ViewK1ActionCell";
import { StatusCellRenderer } from "pages/ClientDocuments/GridStatuses";

const ClientsPage: React.FC<ClientsPageProps> = ({ transporter }) => {

  const [clientsSearching, setClientsSearching] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [clientData, setClientData] = useState<ClientDataItem[]>([]);
  const [clientNameOrId, setClientNameOrId] = useState<string>("");
  const [clientDocumentsShow, setClientDocumentsShow] = useState<boolean>(false);
  const [clientMasterId, setClientMasterId] = useState<number | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [clientOffice, setClientOffice] = useState<string | null>(null);
  const [clientScope, setClientScope] = useState<ClientScope>("k1");
  const gridRef = useRef(null);
  const [key, setKey] = useState<number | string | undefined>(undefined);

  const getAllClients = useCallback(async (scope: ClientScope = clientScope) => {
    setRefreshing(true);
    try {
      const allClients: ClientDataItem[] = await getClients(scope);
      if (allClients !== undefined) {
        setClientData(allClients);
        setKey(`${scope}-${allClients.length}`);
      }
    } finally {
      setRefreshing(false);
    }
  }, [clientScope]);

  const getSearchedClientData = useCallback(async (
    value: string,
    scope: ClientScope = clientScope
  ) => {
    setClientsSearching(true);
    try {
      const searchedClients: ClientDataItem[] = await searchClient(
        { ClientDetail: value },
        scope
      );
      if (searchedClients !== undefined) {
        setClientData(searchedClients);
        setKey(`${scope}-${JSON.stringify(searchedClients.map(c => c.ClientMasterId))}`);
      }
    } finally {
      setClientsSearching(false);
    }
  }, [clientScope]);

  const loadClients = useCallback(async (
    scope: ClientScope = clientScope,
    searchText: string = clientNameOrId
  ) => {
    const trimmedSearchText = searchText.trim();
    if (trimmedSearchText) {
      await getSearchedClientData(trimmedSearchText, scope);
      return;
    }
    await getAllClients(scope);
  }, [clientNameOrId, clientScope, getAllClients, getSearchedClientData]);

  useEffect(() => {
    void loadClients(clientScope, clientNameOrId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClientScopeChange = (scope: ClientScope) => {
    if (scope === clientScope) return;
    setClientScope(scope);
    void loadClients(scope, clientNameOrId);
  };

  const viewClientDocuments = useCallback((client: ClientDataItem) => {
    setClientMasterId(client.ClientMasterId);
    setClientId(client.ClientId);
    setClientName(client.ClientName);
    setClientOffice(client.Office);
    setClientDocumentsShow(true);
  }, []);

  const rowSelection = useMemo(() => ({
    mode: "singleRow",
    checkboxes: false,
    enableClickSelection: false,
    isRowSelectable: () => false,
  }), []);

  const columnDefs = useMemo(() => [
    {
      headerName: "Client Name",
      field: "ClientName",
      flex: 2,
      sortable: false,
      cellRenderer: GridEntityNameCell,
    },
    {
      headerName: "Client ID",
      field: "ClientId",
      flex: 1,
      sortable: false,
    },
    {
      headerName: "Client Type",
      field: "ClientType",
      flex: 1,
      sortable: false,
    },
    {
      headerName: "Office",
      field: "Office",
      flex: 1,
      sortable: false,
    },
    {
      headerName: "Import Status",
      field: "ImportStatus",
      flex: 1,
      sortable: false,
      cellRenderer: StatusCellRenderer,
      cellStyle: { overflow: "visible" },
    },
    {
      headerName: "Actions",
      field: "",
      cellRenderer: ViewK1ActionCell,
      cellRendererParams: { onViewDocuments: viewClientDocuments },
      pinned: "right" as const,
      maxWidth: 130,
      minWidth: 130,
      sortable: false,
      suppressMenu: true,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
    },
  ], [viewClientDocuments]);

  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void loadClients();
    }
  };

  return (
    <>
      {clientDocumentsShow && (
        <ClientDocumentsPage
          show={clientDocumentsShow}
          onHide={() => setClientDocumentsShow(false)}
          clientmasterid={clientMasterId}
          clientid={clientId}
          clientname={clientName}
          clientoffice={clientOffice}
          transporter={transporter}
        />
      )}

      <div className={styles.pageWrapper}>
        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderLeft}>
            <div className={styles.pageIcon}>
              <Icon.PersonGear />
            </div>
            <div>
              <h4 className={styles.pageTitle}>Clients</h4>
              <p className={styles.pageSubtitle}>
                View and manage all your clients, and seamlessly import their K1s into CCH
              </p>
            </div>
          </div>
        </div>

        <div className={styles.contentCard}>
          <div className={styles.cardHeaderRow}>
            <div className={styles.clientScopeToggle}>
              <button
                type="button"
                className={`${styles.clientScopeToggleBtn} ${clientScope === "k1" ? styles.clientScopeToggleActive : ""}`}
                onClick={() => handleClientScopeChange("k1")}
                aria-pressed={clientScope === "k1"}
              >
                K-1 Clients
              </button>
              <button
                type="button"
                className={`${styles.clientScopeToggleBtn} ${clientScope === "all" ? styles.clientScopeToggleActive : ""}`}
                onClick={() => handleClientScopeChange("all")}
                aria-pressed={clientScope === "all"}
              >
                All Clients
              </button>
            </div>

            <div className={styles.headerActions}>
              <GridSearchInput
                value={clientNameOrId}
                placeholder="Search by Client Name or Client ID"
                onChange={setClientNameOrId}
                onSearch={() => void loadClients()}
                disabled={clientsSearching}
                loading={clientsSearching}
                width={320}
                height={36}
                onKeyDown={handleKeyPress}
              />

              <button
                type="button"
                onClick={() => void loadClients()}
                className={styles.refreshButton}
                aria-label="Refresh grid"
              >
                {refreshing || clientsSearching ? (
                  <ThreeCircles
                    height="15"
                    width="15"
                    visible={true}
                    ariaLabel="three-circles-rotating"
                    outerCircleColor="var(--theme1_primary)"
                    innerCircleColor="var(--theme1_secondary)"
                    middleCircleColor="var(--theme1_tertiary)"
                  />
                ) : (
                  <Icon.ArrowClockwise />
                )}
              </button>
            </div>
          </div>

          <div className={styles.gridShell}>
            <CygnusGrid
              key={key}
              ref={gridRef}
              columnDefs={columnDefs}
              fetchData={async () => ({
                records: clientData,
                totalCount: clientData.length,
              })}
              dataIdentifier="ClientMasterId"
              rowSelection={rowSelection as any}
              cachedIsLoading={refreshing || clientsSearching}
              cachedData={clientData}
              setCachedData={setClientData}
              suppressRowHoverHighlight={true}
              pageSize={30}
              rowHeight={40}
              rowModelType="clientSide"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientsPage;
